import { ReactNode } from 'react';
import { Store } from '../../core/createStore';
import StoreEntity from '../../entity/store';
import User from '../../entity/user';
import BugsnagService from '../../service/bugsnagService';
import SessionRepository from '../../service/sessionRepository/sessionRepository';
import UserRepository from '../../service/userRepository';
import StoreRepository from '../../service/storeRepository';
import { Modal } from "antd";

import { set as setStore } from '../../state/currentStore';
import { dispose as disposeSession, set as setSession } from '../../state/currentSession';
import { dispose as disposeUser, set as setUser } from '../../state/currentUser';

class Authentication {
  private sessionRepository: SessionRepository;
  private userRepository: UserRepository;
  private storeRepository: StoreRepository;
  private store: Store;
  private bugsnagService: BugsnagService;

  constructor ({
                 userRepository,
                 storeRepository,
                 sessionRepository,
                 bugsnagService,
                 store,
               }: {
    userRepository: UserRepository;
    storeRepository: StoreRepository;
    sessionRepository: SessionRepository;
    bugsnagService: BugsnagService;
    store: Store;
  }) {
    this.userRepository = userRepository;
    this.storeRepository = storeRepository;
    this.sessionRepository = sessionRepository;
    this.bugsnagService = bugsnagService;
    this.store = store;
  }

  public async signIn (email: string, password: string): Promise<void> {
    const session = await this.sessionRepository
      .signIn({ email, password })
      .catch(err => {
        this.showError({
          title: 'サインインに失敗',
          messages: [
            'メールアドレス・パスワードを確認してください',
          ],
          err,
        });
        throw err;
      });

    try {
      const user = await this.userRepository.getUserBySession(session);
      this.setUser(user);
    } catch (err) {
      throw err;
    }

    try {
      this.sessionRepository.writeUserSessionToStorage(session);
      this.store.dispatch(setSession(session));
    } catch (err) {
      this.showError({
        title: 'サインインが必要です',
        messages: ['サインインしてください'],
        err,
      });
      throw new Error('It is cannot have reach here.');
    }
  }

  public async restoreSession (): Promise<void> {
    const session = await this.sessionRepository.getUserSessionFromStorage();

    try {
      if (!session) {
        throw new Error('It is cannot have reach here.');
      }

      const refreshedSession = await this.sessionRepository.refreshByUser(session);
      const user = await this.userRepository.getUserBySession(refreshedSession);
      this.setUser(user);

      this.sessionRepository.writeUserSessionToStorage(refreshedSession);
      this.store.dispatch(setSession(refreshedSession));
    } catch (err) {
      this.sessionRepository.deleteUserSessionFromStorage();
      throw err;
    }
  }

  public signOut (): void {
    const session = this.store.getState().currentSession;

    if (!session) {
      throw new Error('It is cannot have reach here.');
    }

    this.sessionRepository.deleteUserSessionFromStorage();
    this.sessionRepository.signOut();

    this.store.dispatch(disposeSession());
    this.store.dispatch(disposeUser());
  }

  public async getSession (): Promise<void> {
    try {
      await this.restoreSession();
    } catch (err) {
      await this.getGuestSession();
    }

    const store = await this.storeRepository.getStoreBySession();
    this.setStore(store);
  }

  public async getGuestSession (): Promise<void> {
    const session = await this.sessionRepository.getGuestSessionFromStorage();

    try {
      const refreshedSession = await this.sessionRepository.refreshByGuestUser(session);

      this.sessionRepository.writeGuestSessionToStorage(refreshedSession);
      this.store.dispatch(setSession(refreshedSession));
    } catch (err) {
      this.sessionRepository.deleteGuestSessionFromStorage();
    }
  }

  private setStore (store: StoreEntity): void {
    this.store.dispatch(setStore(store));
    this.bugsnagService.setStore(store);
  }

  private setUser (user: User): void {
    this.store.dispatch(setUser(user));
    this.bugsnagService.setUser(user);
  }

  private showError ({
                       title = 'Error',
                       messages,
                       err,
                     }: {
    title?: string,
    messages: ReactNode[],
    err: any,
  }): void {
    if (!err || !err.response) {
      Modal.error({
        title: title,
        content: messages,
      });
      return;
    }
    switch (err.response.status) {
      case 401:
      case 404: {
        Modal.error({
          title: title,
          content: messages,
        });
        break;
      }
      case 403:
      case 503: {
        Modal.error({
          title: 'アクセスが制限されています',
          content: '管理者にお問い合わせください',
        });
        break;
      }
      default: {
        Modal.error({
          title: title,
          content: messages,
        });
        window.console.error(err);
      }
    }
  }
}

export default Authentication;
